import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { StreamGameExportAsync, DownloadExportedGameAsync } from '../services/games';
import { useToast } from '../contexts/toast-context';

export const useGameExport = () => {
  const { onToastOpen } = useToast();
  const [showProgress, setShowProgress] = React.useState(false);
  const [progressInfo, setProgressInfo] = React.useState<{
    current: number;
    total: number;
    message: string;
    phase: 'processing' | 'downloading';
  }>({ current: 0, total: 0, message: '', phase: 'processing' });
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const cancelExport = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setShowProgress(false);
  };

  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const triggerExport = async (gameId: number, gameTitle: string) => {
    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setProgressInfo({ current: 0, total: 0, message: 'Initiating export...', phase: 'processing' });
      setShowProgress(true);

      const safeGameName = gameTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `${safeGameName}_export.zip`;

      await StreamGameExportAsync(
        gameId,
        (event) => {
          if (event.status === 'processing') {
            setProgressInfo({
              current: event.current ?? 0,
              total: event.total ?? 0,
              message: event.message ?? 'Processing...',
              phase: 'processing'
            });
          } else if (event.status === 'completed' && event.downloadToken) {
            setProgressInfo(prev => ({
              current: prev.total,
              total: prev.total,
              message: 'Downloading ZIP package...',
              phase: 'downloading'
            }));

            DownloadExportedGameAsync(event.downloadToken, fileName)
              .then(() => {
                setShowProgress(false);
                abortControllerRef.current = null;
                onToastOpen({
                  style: 'success',
                  title: 'Export Game',
                  body: <div>Game exported successfully.</div>
                });
              })
              .catch((err) => {
                console.error('File download failed:', err);
                setShowProgress(false);
                abortControllerRef.current = null;
                onToastOpen({
                  style: 'error',
                  title: 'Export Game',
                  body: <div>Failed to download the exported game package.</div>
                });
              });
          } else if (event.status === 'error') {
            throw new Error(event.error || 'Failed to export game.');
          }
        },
        controller.signal
      );
    } catch (err: any) {
      if (err.name === 'AbortError') {
        onToastOpen({
          style: 'info',
          title: 'Export Game',
          body: <div>Export process cancelled.</div>
        });
        return;
      }

      console.error(err);
      setShowProgress(false);
      onToastOpen({
        style: 'error',
        title: 'Export Game',
        body: <div>{err.message || 'Failed to export game. Please try again.'}</div>
      });
    }
  };

  const renderExportProgressDialog = () => {
    if (!showProgress) return null;

    return (
      <Dialog
        title={<span className="fw-bold text-primary">Exporting Game...</span>}
        onClose={cancelExport}
        closeIcon={progressInfo.phase === 'processing'}
        width={450}>
        <div className="p-3 text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="fw-semibold text-dark mb-2">{progressInfo.message}</p>
          
          {progressInfo.phase === 'processing' && progressInfo.total > 0 && (
            <div className="w-100 mt-3">
              <div className="progress mb-2" style={{ height: '12px', borderRadius: '6px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                  role="progressbar"
                  style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                  aria-valuenow={progressInfo.current}
                  aria-valuemin={0}
                  aria-valuemax={progressInfo.total}
                />
              </div>
              <small className="text-muted fw-medium">
                Processed {progressInfo.current} of {progressInfo.total} assets
              </small>
            </div>
          )}

          {progressInfo.phase === 'downloading' && (
            <div className="w-100 mt-3">
              <div className="progress mb-2" style={{ height: '12px', borderRadius: '6px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  role="progressbar"
                  style={{ width: '100%' }}
                  aria-valuenow={100}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <small className="text-muted fw-medium">
                Please wait while the download completes...
              </small>
            </div>
          )}
        </div>
      </Dialog>
    );
  };

  return {
    triggerExport,
    renderExportProgressDialog
  };
};
